import React, { useMemo } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import {
  VisibilityOutlined as ViewIcon,
  HighlightOffOutlined as HighlightOffIcon,
} from '@mui/icons-material';
import { BaseTable, TableColumn, ActionButton } from '../../common/Table';
import EmptyStateMessage from '../../common/EmptyStateMessage';
import type { PhysicalCountSession } from '../../../types/physicalCount.types';
import { usePermission } from '../../../hooks/usePermission';
import { INVENTORY_PERMISSIONS } from '../../../constants/permissions.constants';

export interface PhysicalCountTableProps {
  sessions: PhysicalCountSession[];
  loading: boolean;
  onViewSession: (session: PhysicalCountSession) => void;
  onRequestWriteOffSession: (session: PhysicalCountSession) => void;
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const PhysicalCountTable: React.FC<PhysicalCountTableProps> = ({
  sessions,
  loading,
  onViewSession,
  onRequestWriteOffSession,
}) => {
  const canWriteOffMissing = usePermission(INVENTORY_PERMISSIONS.PHYSICAL_COUNT.WRITE_OFF_MISSING);
  const columns: TableColumn<PhysicalCountSession>[] = useMemo(
    () => [
      {
        id: 'code',
        label: 'Código',
        sortAccessor: (item) => item.code,
        render: (item) => (
          <Typography fontWeight={600} fontSize="0.95rem">
            {item.code}
          </Typography>
        ),
      },
      {
        id: 'locationName',
        label: 'Sucursal',
        sortAccessor: (item) => item.locationName,
        render: (item) => (
          <Typography color="text.secondary" fontSize="0.95rem">
            {item.locationName}
          </Typography>
        ),
      },
      {
        id: 'sessionDate',
        label: 'Fecha',
        sortAccessor: (item) => item.sessionDate,
        render: (item) => (
          <Typography color="text.secondary" fontSize="0.95rem">
            {formatDate(item.sessionDate)}
          </Typography>
        ),
      },
      {
        id: 'expectedCount',
        label: 'Esperado',
        align: 'right',
        sortAccessor: (item) => item.expectedCount,
        render: (item) => (
          <Typography fontWeight={500} fontSize="0.95rem">
            {item.expectedCount}
          </Typography>
        ),
      },
      {
        id: 'actualCount',
        label: 'Escaneado',
        align: 'right',
        sortAccessor: (item) => item.actualCount,
        render: (item) => (
          <Typography fontWeight={500} fontSize="0.95rem">
            {item.actualCount}
          </Typography>
        ),
      },
      {
        id: 'okCount',
        label: 'Coincidencias',
        align: 'right',
        sortAccessor: (item) => item.okCount,
        render: (item) => (
          <Typography fontWeight={500} fontSize="0.95rem" sx={{ color: 'success.main' }}>
            {item.okCount}
          </Typography>
        ),
      },
      {
        id: 'missingCount',
        label: 'Faltantes',
        align: 'right',
        sortAccessor: (item) => item.missingCount,
        render: (item) => (
          <Typography
            fontWeight={500}
            fontSize="0.95rem"
            sx={{
              color: item.missingCount > 0 ? 'error.main' : 'text.secondary',
            }}
          >
            {item.missingCount}
          </Typography>
        ),
      },
      {
        id: 'surplusCount',
        label: 'Sobrantes',
        align: 'right',
        sortAccessor: (item) => item.surplusCount,
        render: (item) => (
          <Typography
            fontWeight={500}
            fontSize="0.95rem"
            sx={{
              color: item.surplusCount > 0 ? 'warning.main' : 'text.secondary',
            }}
          >
            {item.surplusCount}
          </Typography>
        ),
      },
      {
        id: 'duplicateCount',
        label: 'Duplicados',
        align: 'right',
        sortAccessor: (item) => item.duplicateCount,
        render: (item) => (
          <Typography
            fontWeight={500}
            fontSize="0.95rem"
            sx={{
              color: item.duplicateCount > 0 ? 'warning.main' : 'text.secondary',
            }}
          >
            {item.duplicateCount}
          </Typography>
        ),
      },
      {
        id: 'scannedNotAvailableCount',
        label: 'No disponibles',
        align: 'right',
        sortAccessor: (item) => item.scannedNotAvailableCount ?? 0,
        render: (item) => (
          <Typography
            fontWeight={500}
            fontSize="0.95rem"
            sx={{
              color: (item.scannedNotAvailableCount ?? 0) > 0 ? 'info.main' : 'text.secondary',
            }}
          >
            {item.scannedNotAvailableCount ?? 0}
          </Typography>
        ),
      },
      {
        id: 'createdBy',
        label: 'Realizado por',
        sortAccessor: (item) => item.createdBy,
        render: (item) => (
          <Typography color="text.secondary" fontSize="0.95rem">
            {item.createdBy || '-'}
          </Typography>
        ),
      },
    ],
    [],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (sessions.length === 0) {
    return <EmptyStateMessage message="No hay sesiones de conteo físico para mostrar." />;
  }

  return (
    <BaseTable<PhysicalCountSession>
      items={sessions}
      columns={columns}
      getItemId={(item) => item.id}
      showActions
      emptyStateTitle="No hay sesiones de conteo físico para mostrar."
      sortBy="sessionDate"
      sortOrder="desc"
      renderActions={(item) => {
        const canWriteOff =
          canWriteOffMissing && item.status === 'finalized' && item.missingCount > 0;
        const writeOffTooltip = !canWriteOffMissing
          ? 'Sin permiso para dar de baja faltantes'
          : item.status !== 'finalized'
            ? 'Solo disponible cuando la sesión está finalizada'
            : item.missingCount <= 0
              ? 'No hay faltantes en esta sesión'
              : 'Confirmar faltantes';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Tooltip title="Ver detalle" arrow>
              <ActionButton
                size="small"
                onClick={() => onViewSession(item)}
                aria-label="Ver detalle"
              >
                <ViewIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
            <Tooltip title={writeOffTooltip} arrow>
              <span>
                <ActionButton
                  size="small"
                  disabled={!canWriteOff}
                  onClick={() => canWriteOff && onRequestWriteOffSession(item)}
                  aria-label="Confirmar faltantes"
                >
                  <HighlightOffIcon fontSize="small" />
                </ActionButton>
              </span>
            </Tooltip>
          </Box>
        );
      }}
    />
  );
};

export default PhysicalCountTable;
